import React from 'react';
import { PipelineFilterState } from '@/types/domain/pipeline';
import { Search, Filter, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface PipelineFiltersProps {
  filters: PipelineFilterState;
  onFilterChange: (updated: Partial<PipelineFilterState>) => void;
  onResetFilters: () => void;
  activeCount?: number;
}

export const PipelineFilters: React.FC<PipelineFiltersProps> = ({
  filters,
  onFilterChange,
  onResetFilters,
}) => {
  const isFiltered =
    filters.search !== '' ||
    filters.stage !== 'ALL' ||
    filters.department !== 'ALL' ||
    filters.category !== 'ALL' ||
    filters.isDelayedOnly ||
    filters.isBlockedOnly;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3.5 shadow-2xs font-poppins">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-b border-border/60 pb-3">
        <div className="flex items-center gap-2 text-sm font-bold text-foreground font-poppins">
          <Filter className="h-4 w-4 text-primary" />
          <span>Pipeline Filters & Controls</span>
        </div>

        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="h-8 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-500/10 font-poppins cursor-pointer"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Clear All Filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* Search Field */}
        <div className="lg:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onFilterChange({ search: e.target.value, page: 1 })}
            placeholder="Search REQ, SAP PR, PO, Vendor, Invoice..."
            className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-2 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-poppins"
          />
        </div>

        {/* Current Stage Filter */}
        <div>
          <select
            value={filters.stage}
            onChange={(e) => onFilterChange({ stage: e.target.value as any, page: 1 })}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-poppins"
          >
            <option value="ALL">All 12 Stages</option>
            <option value="REQUEST">1. Requisition</option>
            <option value="APPROVAL">2. Approval</option>
            <option value="SAP_PR">3. SAP PR Sync</option>
            <option value="RFQ">4. RFQ Sourcing</option>
            <option value="QUOTATION">5. Quotations</option>
            <option value="SUPPLIER">6. Vendor Selection</option>
            <option value="PO">7. Purchase Order</option>
            <option value="DELIVERY">8. Delivery</option>
            <option value="GRN">9. Goods Receipt</option>
            <option value="INVOICE">10. 3-Way Match</option>
            <option value="PAYMENT">11. Payment</option>
            <option value="CLOSED">12. Closed</option>
          </select>
        </div>

        {/* Department Filter */}
        <div>
          <select
            value={filters.department}
            onChange={(e) => onFilterChange({ department: e.target.value, page: 1 })}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-poppins"
          >
            <option value="ALL">All Departments</option>
            <option value="Engineering">Engineering</option>
            <option value="Operations">Operations</option>
            <option value="Information Technology">Information Technology</option>
            <option value="Manufacturing">Manufacturing</option>
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <select
            value={filters.category}
            onChange={(e) => onFilterChange({ category: e.target.value, page: 1 })}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-poppins"
          >
            <option value="ALL">All Categories</option>
            <option value="IT Hardware">IT Hardware</option>
            <option value="Facilities & Services">Facilities & Services</option>
            <option value="Consulting Services">Consulting Services</option>
            <option value="Raw Materials">Raw Materials</option>
          </select>
        </div>

        {/* Quick Exception Checkbox Filters */}
        <div className="flex items-center gap-3 text-xs font-bold text-foreground font-poppins">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filters.isDelayedOnly}
              onChange={(e) => onFilterChange({ isDelayedOnly: e.target.checked, page: 1 })}
              className="rounded border-input text-amber-600 focus:ring-amber-500 cursor-pointer"
            />
            <span className="text-amber-700 dark:text-amber-300">Delayed</span>
          </label>

          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filters.isBlockedOnly}
              onChange={(e) => onFilterChange({ isBlockedOnly: e.target.checked, page: 1 })}
              className="rounded border-input text-rose-600 focus:ring-rose-500 cursor-pointer"
            />
            <span className="text-rose-700 dark:text-rose-300">Blocked</span>
          </label>
        </div>
      </div>
    </div>
  );
};
